import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import { api } from '../services/api'
import { useDebounce } from '../hooks'
import { Badge, Banner, Button, Panel } from '../components'
import pageStyles from '../styles/page.module.scss'
import formStyles from '../styles/forms.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import buttonStyles from '../components/Button.module.scss'
import styles from './CupTeams.module.scss'
import { cupTeamName } from '../utils/cupTeams'

interface CupTeamSummary {
  tc_cup_team_id: string
  ext_competitor_id: string | null
  tc_name: string | null
  ext_name: string | null
  abbreviation: string | null
  image_url: string | null
  image_source: 'logo' | 'flag' | null
  season_count: number
}

const CupTeamsList: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newAbbr, setNewAbbr] = useState('')
  const [newCountry, setNewCountry] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const debouncedSearch = useDebounce(searchTerm, 300)

  const { data, isLoading, error, mutate } = useSWR(
    ['cup-teams', debouncedSearch],
    () => api.get<{ cup_teams: CupTeamSummary[] }>('/cup-teams', { name: debouncedSearch?.trim() || undefined }),
    { revalidateOnFocus: false },
  )
  const teams = data?.cup_teams || []

  const createTeam = async () => {
    setBusy(true)
    setCreateError(null)
    try {
      const created = await api.post<{ cup_team: { tc_cup_team_id: string } }>('/cup-teams', {
        tc_name: newName.trim(),
        tc_abbreviation: newAbbr.trim() || null,
        tc_country_code: newCountry.trim().toUpperCase() || null,
      })
      navigate(`/cup-teams/${created.cup_team.tc_cup_team_id}`)
    } catch (err) {
      setCreateError((err as Error).message)
      setBusy(false)
    }
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading cup teams</h2>
          <p>{(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.pageHeader}>
        <h1>Cup Teams</h1>
        <div className={pageStyles.headerButtons}>
          <Button onClick={() => setCreating(true)} disabled={creating}>
            + New Cup Team
          </Button>
        </div>
      </div>
      <p className={styles.muted}>
        Nations from Davis Cup, Billie Jean King Cup, United Cup and Hopman Cup, and editorial teams such as Laver Cup&apos;s
        Team Europe and Team World.
      </p>

      {creating && (
        <Panel>
          <Panel.Header>
            <h2>New cup team</h2>
          </Panel.Header>
          {createError && <Banner variant="error" message={createError} onDismiss={() => setCreateError(null)} />}
          <div className={formStyles.fieldGroup}>
            <label htmlFor="new-cup-team-name">Name</label>
            <input
              id="new-cup-team-name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Team Europe"
              disabled={busy}
            />
          </div>
          <div className={formStyles.fieldGroup}>
            <label htmlFor="new-cup-team-abbr">Abbreviation</label>
            <input
              id="new-cup-team-abbr"
              type="text"
              value={newAbbr}
              onChange={(e) => setNewAbbr(e.target.value)}
              placeholder="EUR"
              disabled={busy}
            />
          </div>
          <div className={formStyles.fieldGroup}>
            <label htmlFor="new-cup-team-country">Country code (flag), for a nation</label>
            <input
              id="new-cup-team-country"
              type="text"
              value={newCountry}
              onChange={(e) => setNewCountry(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className={styles.toolbar}>
            <Button onClick={createTeam} disabled={busy || !newName.trim()}>
              Create team
            </Button>
            <Button variant="secondary" onClick={() => setCreating(false)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </Panel>
      )}

      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              id="cup-team-search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search cup teams by name..."
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading cup teams...</h3>
        </div>
      ) : teams.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No cup teams found</h3>
          <p>{debouncedSearch ? `No teams match "${debouncedSearch}".` : 'No cup teams have been ingested yet.'}</p>
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Image</div>
            <div>Team</div>
            <div>Sportradar ID</div>
            <div>Seasons</div>
            <div>Actions</div>
          </div>
          <div className={listTableStyles.tableBody}>
            {teams.map((team) => (
              <div key={team.tc_cup_team_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                <div>
                  {team.image_url ? (
                    <img className={styles.listImage} src={team.image_url} alt="" title={team.image_source === 'logo' ? 'Team logo' : 'Country flag'} />
                  ) : (
                    <Badge variant="neutral" size="small">No image</Badge>
                  )}
                </div>
                <div className={listTableStyles.cellPrimary}>
                  <p className={listTableStyles.primaryText}>{cupTeamName(team)}</p>
                  {team.abbreviation && <p className={listTableStyles.secondaryText}>({team.abbreviation})</p>}
                </div>
                <div className={listTableStyles.cellId}>
                  {team.ext_competitor_id || <Badge variant="secondary" size="small">Editorial</Badge>}
                </div>
                <div className={listTableStyles.cellText}>{team.season_count}</div>
                <div className={listTableStyles.cellActions}>
                  <Link
                    to={`/cup-teams/${team.tc_cup_team_id}`}
                    className={`${buttonStyles.button} ${buttonStyles.primary} ${buttonStyles.medium}`}
                  >
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default CupTeamsList
